import React from 'react';
import { Link } from 'react-router-dom';

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  marginBottom: '2rem',
  fontSize: '0.95rem'
};

const thTdStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  padding: '0.75rem 1rem',
  textAlign: 'left',
  verticalAlign: 'top'
};

const BankStatementConverterPrivacyPolicy: React.FC = () => {
  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/" 
            style={{ 
              color: 'var(--accent-color)', 
              textDecoration: 'none',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to Home
          </Link>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #0f4c81 0%, #1a73e8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Bank Statement Converter Privacy Policy
          </h1>
          <div className="section-divider"></div>
        </div>

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          lineHeight: '1.6',
          fontSize: '1rem'
        }}>
          <p style={{ marginBottom: '2rem', fontSize: '1rem' }}>
            <strong>Last Updated:</strong> August 18, 2026<br/>
            <strong>Contact:</strong> info@rapidnova.co
          </p>

          <p style={{ marginBottom: '1rem' }}>
            This Privacy Policy describes how <strong>RapidNova</strong> ("we," "us," or "our") collects, uses, and shares information when you use the iOS app <strong>Bank Statement Converter</strong> (also known as Statements, bundle ID <code>com.statementconverter.app</code>) (the "App").
          </p>
          <p style={{ marginBottom: '2rem' }}>
            If you do not agree with this policy, please do not use the App.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>1. Summary</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thTdStyle}>Topic</th>
                <th style={thTdStyle}>What we do</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={thTdStyle}>Your statements on the device</td>
                <td style={thTdStyle}>Extracted transactions, originals you import, and export files are stored <strong>on your device</strong>.</td>
              </tr>
              <tr>
                <td style={thTdStyle}>Cloud processing during import</td>
                <td style={thTdStyle}>To convert a statement, page <strong>images</strong> are sent securely through our backend to <strong>Segmind AI</strong> for <strong>text extraction only</strong>. We do not retain these images after processing. An internet connection is required to import.</td>
              </tr>
              <tr>
                <td style={thTdStyle}>Accounts</td>
                <td style={thTdStyle}>The App does <strong>not</strong> ask you to create a username/password account. We use an anonymous technical ID for security and subscriptions.</td>
              </tr>
              <tr>
                <td style={thTdStyle}>Ads and selling data</td>
                <td style={thTdStyle}>We do <strong>not</strong> show ads, and we do <strong>not</strong> sell your personal information.</td>
              </tr>
              <tr>
                <td style={thTdStyle}>Analytics / crash tools</td>
                <td style={thTdStyle}>We do <strong>not</strong> use Firebase Analytics, Crashlytics, or similar advertising trackers in the App.</td>
              </tr>
            </tbody>
          </table>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>2. Information We Process</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.1 Information You Provide</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Bank statements you import</strong> — PDF files, scanned pages, or photos of printed statements you choose to process.</li>
            <li><strong>Edits you make</strong> — corrections to transactions, categories, or related review data.</li>
            <li><strong>Exports you create</strong> — files you generate (for example CSV, Excel, OFX/QFX/QIF) and choose to share via the system share sheet.</li>
            <li><strong>Optional preferences</strong> — such as appearance, display currency, date format, default export format, and whether to require Face ID / Touch ID to open the App. These are stored on your device.</li>
            <li><strong>Purchases</strong> — if you buy Pro, Apple processes the payment. We receive subscription status through our payments partner (see below).</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.2 Information Processed Automatically</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Anonymous app identifier</strong> — when the App starts, we create an anonymous Firebase Authentication ID so our backend can verify requests. This is not your name or email.</li>
            <li><strong>App Check / device integrity signals</strong> — we use Apple App Attest (and related App Check mechanisms) to help prevent abuse of our conversion service.</li>
            <li><strong>Subscription identifiers</strong> — we link the anonymous Firebase ID to RevenueCat so we can restore and manage Pro entitlements across launches.</li>
            <li><strong>Technical logs on our servers</strong> — our cloud function may log limited technical details such as anonymous user ID, model name, timing, and errors. We do not use these logs to build advertising profiles.</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.3 Sensitive Financial Content</h3>
          <p style={{ marginBottom: '2rem' }}>
            Bank statements can include account numbers, balances, names, addresses, merchant names, and other financial details. When you import a statement, <strong>images of statement pages</strong> are transmitted for automated text extraction. Treat the App accordingly and only import statements you are comfortable processing this way.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>3. How We Use Information</h2>
          <p style={{ marginBottom: '0.5rem' }}>We use the information above to:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Extract transactions and balances from statements you import</li>
            <li>Show Insights, reviews, and exports on your device</li>
            <li>Operate, secure, and prevent abuse of the conversion service</li>
            <li>Provide and restore Pro subscriptions</li>
            <li>Improve reliability and fix failures in conversion</li>
            <li>Comply with law and enforce our terms</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}>We do <strong>not</strong> use your statement contents for advertising.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>4. How Conversion Works</h2>
          <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>You import a PDF or scan on your device.</li>
            <li>The App prepares page images and sends them to our <strong>Firebase Cloud Function</strong>.</li>
            <li>That function calls <strong>Segmind AI</strong> (currently using a Qwen vision model) to extract structured transaction data from statement page images for <strong>text extraction only</strong>.</li>
            <li>The extracted result is returned to the App and saved <strong>on your device</strong>.</li>
            <li>Your original statement file and extracted data remain in on-device storage unless you delete them or uninstall the App.</li>
          </ol>
          <p style={{ marginBottom: '2rem' }}>
            We do <strong>not</strong> operate a Firebase Storage / cloud library of your statements in the App. Persistence is local (on-device database and files).
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>5. Where Information Is Stored</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>On Your Device</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Extracted statements and transactions</li>
            <li>Copies of imported source files</li>
            <li>Export history files you generate</li>
            <li>App settings and free-conversion usage counters</li>
          </ul>
          <p style={{ marginBottom: '1rem' }}>
            You can delete local statements, source files, and exports from in-app storage controls (Settings → Manage Storage), or by deleting the App (which removes local App data from that device).
          </p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>Off Your Device (During / For Service Operation)</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Statement <strong>page images</strong> and extraction prompts, while being processed by our backend and <strong>Segmind AI</strong> for text extraction only</li>
            <li>Anonymous Firebase Auth / App Check data</li>
            <li>Subscription status via RevenueCat and Apple</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}>
            We ask service providers to process data only to provide their services to us. For statement conversion, page images are processed transiently and are <strong>not retained</strong> after text extraction. We do not provide a cloud statement library and do not intentionally keep a permanent cloud archive of statement images.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>6. Sharing of Information</h2>
          <p style={{ marginBottom: '0.5rem' }}>We share information only as needed to run the App:</p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thTdStyle}>Recipient</th>
                <th style={thTdStyle}>Why</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={thTdStyle}><strong>Google Firebase</strong> (Auth, App Check, Cloud Functions)</td>
                <td style={thTdStyle}>Authenticate anonymous requests, protect the API, run conversion</td>
              </tr>
              <tr>
                <td style={thTdStyle}><strong>Segmind AI</strong></td>
                <td style={thTdStyle}>Processes statement page images for text/transaction extraction only; images are not retained after processing</td>
              </tr>
              <tr>
                <td style={thTdStyle}><strong>RevenueCat</strong></td>
                <td style={thTdStyle}>Subscription status, purchases, restores</td>
              </tr>
              <tr>
                <td style={thTdStyle}><strong>Apple</strong></td>
                <td style={thTdStyle}>App distribution, In-App Purchases, optional Face ID / Touch ID on device, system share sheet when you export</td>
              </tr>
            </tbody>
          </table>
          <p style={{ marginBottom: '1rem' }}>
            We do <strong>not</strong> sell personal information. We do <strong>not</strong> share statement contents with advertisers.
          </p>
          <p style={{ marginBottom: '2rem' }}>
            We may disclose information if required by law, to protect rights and safety, or in connection with a business transfer (for example a merger), in which case we will take reasonable steps to keep protections in place.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>7. Permissions the App May Request</h2>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Camera</strong> — to scan printed statements.</li>
            <li><strong>Face ID / Touch ID</strong> — optional lock so others with your unlocked phone cannot open the App. Biometric templates stay in Apple's secure enclave; we only store your on/off preference.</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}>
            The App does not request access to your full photo library, contacts, or precise location.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>8. Subscriptions and Payments</h2>
          <p style={{ marginBottom: '2rem' }}>
            Payments are processed by <strong>Apple</strong>. We do not receive your full payment card number. Subscription management and restores may involve <strong>RevenueCat</strong> using an anonymous app user ID.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>9. Children's Privacy</h2>
          <p style={{ marginBottom: '2rem' }}>
            The App is not directed to children under 13 (or the minimum age required in your country). We do not knowingly collect personal information from children. If you believe a child has provided information through the App, contact us and we will take appropriate steps.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>10. International Processing</h2>
          <p style={{ marginBottom: '2rem' }}>
            Our service providers may process data in the United States or other countries. If you use the App from outside those countries, you consent to transfer of the data described in this policy for the purposes above.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>11. Your Choices and Rights</h2>
          <p style={{ marginBottom: '1rem' }}>
            Depending on where you live (including the EEA/UK and certain U.S. states), you may have rights to access, correct, delete, or restrict certain personal data, or to object to certain processing.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>Because most statement data lives on your device, the fastest way to delete it is usually:</p>
          <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Use <strong>Manage Storage</strong> / delete statements in the App, and/or</li>
            <li>Delete the App from your device</li>
          </ol>
          <p style={{ marginBottom: '1rem' }}>
            For questions about cloud processing, anonymous IDs, or subscriptions, contact us at <strong>info@rapidnova.co</strong>. We will respond within a reasonable time.
          </p>
          <p style={{ marginBottom: '2rem' }}>
            If you are in the EEA/UK, you may also lodge a complaint with your local data protection authority.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>12. Data Retention</h2>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>On-device data:</strong> kept until you delete it or remove the App.</li>
            <li><strong>Conversion processing:</strong> page images are transmitted to Segmind AI for text extraction and are not retained after processing. We do not provide a cloud statement library in the App. Server logs are kept only as long as reasonably needed for security, debugging, and abuse prevention.</li>
            <li><strong>Subscription records:</strong> retained as needed to provide Pro and meet accounting/legal obligations (via Apple/RevenueCat).</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>13. Security</h2>
          <p style={{ marginBottom: '2rem' }}>
            We use industry-standard transport security (HTTPS) for cloud calls, Firebase App Check to reduce abuse, and on-device storage for your statement library. No method of transmission or storage is 100% secure. Protect your device with a passcode and consider enabling the App's biometric lock.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>14. Third-Party Policies</h2>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>Apple Privacy Policy</a></li>
            <li><a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>Google / Firebase Privacy</a></li>
            <li><a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>RevenueCat Privacy Policy</a></li>
            <li><a href="https://www.segmind.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>Segmind AI Privacy Policy</a></li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>15. Changes to This Policy</h2>
          <p style={{ marginBottom: '2rem' }}>
            We may update this Privacy Policy from time to time. We will post the updated version with a new "Last updated" date. Continued use of the App after changes means you accept the updated policy, except where applicable law requires additional notice or consent.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>16. Contact</h2>
          <p style={{ marginBottom: '0.5rem' }}><strong>RapidNova</strong></p>
          <p style={{ marginBottom: '0.5rem' }}><strong>Email:</strong> info@rapidnova.co</p>
          <p style={{ marginBottom: '1.5rem' }}>
            60 Paya Lebar Road, #07-54<br/>
            Paya Lebar Square<br/>
            Singapore 409051
          </p>
          <p style={{ marginBottom: '2rem' }}>
            App: Bank Statement Converter (<code>com.statementconverter.app</code>)
          </p>

          <hr style={{ margin: '3rem 0', border: 'none', borderTop: '1px solid #eee' }} />
          <p style={{ marginBottom: '0', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
            This document is the privacy policy for the Bank Statement Converter iOS application.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BankStatementConverterPrivacyPolicy;
